import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Ticket, Plus, MessageCircle, Clock, CheckCircle, 
  AlertCircle, ChevronRight, Send, ArrowLeft, Loader2,
  Crown, Lock, HelpCircle, Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TicketsPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [filter, setFilter] = useState('all');
  
  // New ticket form
  const [newTicket, setNewTicket] = useState({
    subject: '',
    category: 'genel',
    message: '',
    priority: 'normal'
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Reply
  const [replyMessage, setReplyMessage] = useState('');
  const [replying, setReplying] = useState(false);

  const isPro = user?.is_pro || user?.membership?.plan_type === 'pro';

  useEffect(() => {
    const loadTickets = async () => {
      if (isPro) {
        try {
          const response = await axios.get(`${API_URL}/api/tickets`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setTickets(response.data);
        } catch (error) {
          if (error.response?.status !== 403) {
            toast.error('Talepler yüklenemedi');
          }
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };
    loadTickets();
  }, [isPro, token]);

  const fetchTickets = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(response.data);
    } catch (error) {
      if (error.response?.status !== 403) {
        toast.error('Talepler yüklenemedi');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!newTicket.subject || !newTicket.message) {
      toast.error('Lütfen tüm alanları doldurun');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/tickets`, newTicket, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      setShowNewTicket(false);
      setNewTicket({ subject: '', category: 'genel', message: '', priority: 'normal' });
      fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Talep oluşturulamadı');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim()) return;

    setReplying(true);
    try {
      await axios.post(`${API_URL}/api/tickets/${selectedTicket.id}/reply`, 
        { message: replyMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Yanıt gönderildi');
      setReplyMessage('');
      // Refresh ticket
      const response = await axios.get(`${API_URL}/api/tickets/${selectedTicket.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTicket(response.data);
      fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Yanıt gönderilemedi');
    } finally {
      setReplying(false);
    }
  };

  const handleCloseTicket = async () => {
    try {
      await axios.put(`${API_URL}/api/tickets/${selectedTicket.id}/close`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Talep kapatıldı');
      setSelectedTicket(null);
      fetchTickets();
    } catch (error) {
      toast.error('Talep kapatılamadı');
    }
  };

  const getStatusBadge = (status) => {
    const statuses = {
      open: { label: 'Açık', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
      in_progress: { label: 'İşleniyor', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
      resolved: { label: 'Çözüldü', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
      closed: { label: 'Kapalı', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' }
    };
    const s = statuses[status] || statuses.open;
    return <Badge className={`${s.color} border`}>{s.label}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const priorities = {
      low: { label: 'Düşük', color: 'text-slate-400' },
      normal: { label: 'Normal', color: 'text-blue-400' },
      high: { label: 'Yüksek', color: 'text-red-400' }
    };
    const p = priorities[priority] || priorities.normal;
    return <span className={`text-xs ${p.color}`}>{p.label}</span>;
  };

  const getCategoryLabel = (category) => {
    const categories = {
      genel: 'Genel',
      teknik: 'Teknik Destek',
      odeme: 'Ödeme',
      oneri: 'Öneri',
      sinyal: 'Sinyal',
      bildirim: 'Bildirim'
    };
    return categories[category] || category;
  };

  const filteredTickets = filter === 'all' 
    ? tickets 
    : tickets.filter(t => t.status === filter);

  // Non-Pro view
  if (!isPro) {
    return (
      <div className="min-h-screen bg-brand-dark py-12 px-4">
        <div className="container mx-auto max-w-2xl">
          {/* SSS Yönlendirme */}
          <div className="mb-6 p-4 bg-slate-800/50 border border-slate-700 rounded-xl flex items-center gap-3">
            <HelpCircle className="h-5 w-5 text-primary flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-slate-300">
                Sorunuz için önce <Link to="/sss" className="text-primary hover:underline font-medium">Sık Sorulan Sorular</Link> sayfamıza göz atın.
              </p>
            </div>
          </div>

          <Card className="border-amber-500/20 bg-gradient-to-br from-amber-500/5 to-transparent">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="mx-auto bg-amber-500/20 text-amber-400 p-4 rounded-full w-fit mb-6">
                <Crown className="h-10 w-10" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-3">
                Pro Destek Merkezi
              </h1>
              <p className="text-slate-400 mb-6 max-w-md mx-auto">
                Öncelikli destek ve birebir yardım almak için Pro üyeliğe geçin. 
                Ücretsiz kullanıcılar için iletişim formu mevcuttur.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/payment?plan=pro&billing=yearly">
                  <Button className="btn-primary">
                    <Crown className="mr-2 h-4 w-4" />
                    Pro&apos;ya Geç
                  </Button>
                </Link>
                <Link to="/kurumsal/iletisim">
                  <Button variant="outline">
                    <MessageCircle className="mr-2 h-4 w-4" />
                    İletişim Formu
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Ticket Detail View
  if (selectedTicket) {
    return (
      <div className="min-h-screen bg-brand-dark py-8 px-4">
        <div className="container mx-auto max-w-3xl">
          <button
            onClick={() => setSelectedTicket(null)}
            className="flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Tüm Talepler
          </button>

          <Card>
            <CardHeader className="border-b border-white/5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-slate-500">{selectedTicket.ticket_number}</span>
                    {getStatusBadge(selectedTicket.status)}
                    {getPriorityBadge(selectedTicket.priority)}
                  </div>
                  <CardTitle className="text-xl">{selectedTicket.subject}</CardTitle>
                  <p className="text-sm text-slate-400 mt-1">
                    {getCategoryLabel(selectedTicket.category)} • {new Date(selectedTicket.created_at).toLocaleDateString('tr-TR')}
                  </p>
                </div>
                {selectedTicket.status !== 'closed' && (
                  <Button variant="outline" size="sm" onClick={handleCloseTicket}>
                    Kapat
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {/* Messages */}
              <div className="space-y-4 mb-6">
                {selectedTicket.messages?.map((msg, index) => (
                  <div
                    key={msg.id || index}
                    className={`p-4 rounded-lg ${
                      msg.sender === 'admin'
                        ? 'bg-primary/10 border border-primary/20 ml-8'
                        : 'bg-white/5 border border-white/10 mr-8'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-sm font-medium ${msg.sender === 'admin' ? 'text-primary' : 'text-white'}`}>
                        {msg.sender === 'admin' ? 'Destek Ekibi' : msg.sender_name || 'Sen'}
                      </span>
                      <span className="text-xs text-slate-500">
                        {new Date(msg.created_at).toLocaleString('tr-TR')}
                      </span>
                    </div>
                    <p className="text-slate-300 text-sm whitespace-pre-wrap">{msg.message}</p>
                  </div>
                ))}
              </div>

              {/* Reply Form */}
              {selectedTicket.status !== 'closed' && (
                <form onSubmit={handleReply} className="border-t border-white/5 pt-4">
                  <textarea
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    placeholder="Yanıtınızı yazın..."
                    className="w-full p-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-primary/50 resize-none"
                    rows={3}
                  />
                  <div className="flex justify-end mt-3">
                    <Button type="submit" disabled={replying || !replyMessage.trim()}>
                      {replying ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="mr-2 h-4 w-4" />
                      )}
                      Yanıt Gönder
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Ticket List View
  return (
    <div className="min-h-screen bg-brand-dark py-8 px-4">
      <div className="container mx-auto max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Ticket className="h-7 w-7 text-primary" />
              Destek Talepleri
            </h1>
            <p className="text-slate-400 text-sm mt-1">Pro özel destek merkezi</p>
          </div>
          <Button onClick={() => setShowNewTicket(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Yeni Talep
          </Button>
        </div>

        {/* SSS Yönlendirme */}
        <div className="mb-6 p-3 bg-slate-800/30 border border-slate-700/50 rounded-lg flex items-center gap-3">
          <HelpCircle className="h-4 w-4 text-slate-400 flex-shrink-0" />
          <p className="text-xs text-slate-400">
            Talep oluşturmadan önce <Link to="/sss" className="text-primary hover:underline">SSS</Link> sayfamıza göz atın, sorunuzun cevabı orada olabilir.
          </p>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { value: 'all', label: 'Tümü' },
            { value: 'open', label: 'Açık' },
            { value: 'in_progress', label: 'İşleniyor' },
            { value: 'resolved', label: 'Çözüldü' },
            { value: 'closed', label: 'Kapalı' }
          ].map((f) => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                filter === f.value
                  ? 'bg-primary text-brand-dark'
                  : 'bg-white/5 text-slate-300 hover:bg-white/10'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Ticket List */}
        {filteredTickets.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <HelpCircle className="h-12 w-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">
                {filter === 'all' ? 'Henüz destek talebiniz yok' : 'Bu filtreye uygun talep yok'}
              </p>
              <Button onClick={() => setShowNewTicket(true)} className="mt-4">
                <Plus className="mr-2 h-4 w-4" />
                İlk Talebini Oluştur
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredTickets.map((ticket) => (
              <motion.div
                key={ticket.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card 
                  className="cursor-pointer hover:border-primary/30 transition-colors"
                  onClick={() => setSelectedTicket(ticket)}
                >
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-slate-500">{ticket.ticket_number}</span>
                          {getStatusBadge(ticket.status)}
                          {getPriorityBadge(ticket.priority)}
                        </div>
                        <h3 className="text-white font-medium truncate">{ticket.subject}</h3>
                        <p className="text-sm text-slate-400 mt-1">
                          {getCategoryLabel(ticket.category)} • {new Date(ticket.updated_at).toLocaleDateString('tr-TR')}
                        </p>
                      </div>
                      <ChevronRight className="h-5 w-5 text-slate-500" />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* New Ticket Modal */}
        <AnimatePresence>
          {showNewTicket && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setShowNewTicket(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="w-full max-w-lg"
                onClick={(e) => e.stopPropagation()}
              >
                <Card>
                  <CardHeader>
                    <CardTitle>Yeni Destek Talebi</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleCreateTicket} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">Konu</label>
                        <input
                          type="text"
                          value={newTicket.subject}
                          onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                          placeholder="Talebinizin konusu"
                          className="w-full p-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-primary/50"
                          required
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-1">Kategori</label>
                          <select
                            value={newTicket.category}
                            onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value })}
                            className="w-full p-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-primary/50"
                          >
                            <option value="genel">Genel</option>
                            <option value="teknik">Teknik Destek</option>
                            <option value="sinyal">Sinyal</option>
                            <option value="bildirim">Bildirim</option>
                            <option value="odeme">Ödeme</option>
                            <option value="oneri">Öneri</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-1">Öncelik</label>
                          <select
                            value={newTicket.priority}
                            onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
                            className="w-full p-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-primary/50"
                          >
                            <option value="low">Düşük</option>
                            <option value="normal">Normal</option>
                            <option value="high">Yüksek</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">Mesajınız</label>
                        <textarea
                          value={newTicket.message}
                          onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
                          placeholder="Sorununuzu veya talebinizi detaylı açıklayın..."
                          className="w-full p-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-primary/50 resize-none"
                          rows={5}
                          required
                        />
                      </div>

                      <div className="flex gap-3 pt-2">
                        <Button type="button" variant="outline" onClick={() => setShowNewTicket(false)} className="flex-1">
                          İptal
                        </Button>
                        <Button type="submit" className="flex-1" disabled={submitting}>
                          {submitting ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <Send className="mr-2 h-4 w-4" />
                          )}
                          Gönder
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default TicketsPage;
